import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { CircleAlert } from 'lucide-react';
import { getCourseDropOff } from '../../api/analytics';
import { formatNumber, formatPercent } from '../../utils/datetime';
import { useCourseTitle } from '../../hooks/useCourseTitle';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import PageHead from '../../components/PageHead/PageHead';
import ProgressBar from '../../components/ProgressBar/ProgressBar';
import TableCard from '../../components/TableCard/TableCard';
import { useI18n } from '../../i18n/I18nContext';
import './DropoffAnalysis.css';

// More rows than this and the table caps its height, so its head stays in view while it scrolls.
const TALL_ROWS = 12;

function DropoffAnalysis() {
  const { t } = useI18n();
  const { courseId } = useParams();
  const courseTitle = useCourseTitle(courseId);
  const [dropoffData, setDropoffData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [denied, setDenied] = useState(false);

  const fetchDropoffData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCourseDropOff(courseId);
      // { courseId, dropOffLessons: [{ lessonId, title, studentsThatDropped, dropOffRate }], ... }
      // The API lists only the lessons that lose 20% or more of the students who finished the
      // lesson before them, in course order — every row here is already a drop-off point.
      setDropoffData(data?.dropOffLessons || []);
      setError(null);
      setDenied(false);
    } catch (err) {
      setDenied(err.code === 403);
      setError(err.code === 403
        ? t('courseInsights.dropoff.accessDenied')
        : t('courseInsights.dropoff.errorLoad'));
    } finally {
      setLoading(false);
    }
  }, [courseId, t]);

  useEffect(() => {
    fetchDropoffData();
  }, [courseId, fetchDropoffData]);

  const header = (
    <PageHead
      back={{ to: `/courses/edit/${courseId}?tab=analytics`, label: t('courseInsights.common.backToAnalytics') }}
      eyebrow={<span className="drp-eyebrow">{courseTitle || t('courseInsights.common.parent')}</span>}
      title={t('courseInsights.dropoff.title')}
      description={t('courseInsights.dropoff.subtitle')}
    />
  );

  if (loading) {
    return (
      <div className="ui-stack drp-page">
        {header}
        <LoadingState skeleton="table" rows={6} message={t('courseInsights.dropoff.loading')} />
      </div>
    );
  }

  return (
    <div className="ui-stack drp-page">
      {header}

      {error && dropoffData.length > 0 && (
        <div className="ui-alert" role="alert">
          <CircleAlert aria-hidden="true" />
          <p>{error}</p>
          <button type="button" className="ds-btn ds-btn-secondary ds-btn-sm" onClick={fetchDropoffData}>
            {t('ui.retry')}
          </button>
        </div>
      )}

      {dropoffData.length === 0 ? (
        error ? (
          <PageEmptyState
            variant={denied ? 'forbidden' : 'error'}
            description={error}
            onRetry={denied ? undefined : fetchDropoffData}
          />
        ) : (
          // Nothing flagged is good news, not missing data
          <PageEmptyState
            variant="success"
            title={t('courseInsights.dropoff.emptyTitle')}
            description={t('courseInsights.dropoff.emptyDescription')}
          />
        )
      ) : (
        <TableCard
          className="drp-table-card"
          tall={dropoffData.length > TALL_ROWS}
          aria-label={t('courseInsights.dropoff.title')}
          footer={(
            <span className="drp-count">
              {t('courseInsights.dropoff.countLessons', { count: formatNumber(dropoffData.length, { decimals: 0 }) })}
            </span>
          )}
        >
          <table className="ui-table ui-table--stack drp-table">
            <thead>
              <tr>
                <th>{t('courseInsights.dropoff.colLesson')}</th>
                <th className="drp-th-rate">{t('courseInsights.dropoff.colRate')}</th>
                <th className="ui-num">{t('courseInsights.dropoff.colStudentsDropped')}</th>
              </tr>
            </thead>
            <tbody>
              {dropoffData.map((lesson) => {
                const rate = Math.max(0, Number(lesson.dropOffRate) || 0);
                return (
                  <tr key={lesson.lessonId}>
                    <td className="drp-lesson">{lesson.title}</td>
                    <td data-label={t('courseInsights.dropoff.colRate')}>
                      <div className="drp-rate">
                        {/* A drop-off is never "complete": the bar stays warm all the way to 100% */}
                        <ProgressBar
                          className="drp-meter"
                          value={Math.min(rate, 100)}
                          label={t('courseInsights.dropoff.colRate')}
                          tone="coral"
                          complete={false}
                        />
                        <span className="drp-pct">{formatPercent(rate)}</span>
                      </div>
                    </td>
                    <td className="ui-num" data-label={t('courseInsights.dropoff.colStudentsDropped')}>
                      {formatNumber(lesson.studentsThatDropped || 0, { decimals: 0 })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </TableCard>
      )}
    </div>
  );
}

export default DropoffAnalysis;
